import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Checkbox,
  Avatar,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery, useMutation } from "@apollo/client";
import { GET_STAFF_DETAILS } from "../../apis/organization-apis/people-apis/peopleApis";
import { SEND_REMINDER_TO_EMPLOYEE } from "../../apis/settingsApis/assets-apis/assetsApis";
import DialogTable from "../../components/DialogTable/DialogTable";

const getInitials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

const getPrimaryEmail = (emp) =>
  emp?.emails?.find((e) => e.is_primary)?.email || emp?.emails?.[0]?.email || "";

const getPrimaryRole = (emp) => emp?.roles?.[0]?.role_name || "";
const getPrimaryJobTitle = (emp) => emp?.employments?.[0]?.job_title || "";

const SendAgentReminderDialog = ({ open, onClose, onSuccess, enrolledOwnerEmails = [] }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [tableFilters, setTableFilters] = useState({});
  const [selected, setSelected] = useState(new Set());

  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS, {
    variables: { filter: { in_scope: true } },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const [sendReminder, { loading: sending }] = useMutation(SEND_REMINDER_TO_EMPLOYEE, {
    onCompleted: () => {
      handleClose();
      onSuccess?.("Reminders will be sent to the employees shortly.");
    },
  });

  const enrolledSet = useMemo(
    () => new Set(enrolledOwnerEmails.map((e) => e?.toLowerCase())),
    [enrolledOwnerEmails]
  );

  const employees = useMemo(() => {
    const all = staffData?.getEmployees?.data || [];
    return all.filter((emp) => {
      const email = getPrimaryEmail(emp)?.toLowerCase();
      return email && !enrolledSet.has(email);
    });
  }, [staffData, enrolledSet]);

  const roleOptions = useMemo(() =>
    [...new Set(employees.map(getPrimaryRole).filter(Boolean))].sort().map((r) => ({ id: r, label: r })),
    [employees]
  );

  const designationOptions = useMemo(() =>
    [...new Set(employees.map(getPrimaryJobTitle).filter(Boolean))].sort().map((t) => ({ id: t, label: t })),
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (tableFilters.role?.length) list = list.filter((e) => tableFilters.role.includes(getPrimaryRole(e)));
    if (tableFilters.designation?.length) list = list.filter((e) => tableFilters.designation.includes(getPrimaryJobTitle(e)));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => e.name?.toLowerCase().includes(q) || getPrimaryEmail(e)?.toLowerCase().includes(q));
    }
    return list;
  }, [employees, tableFilters, search]);

  const handleFilterChange = useCallback((f) => setTableFilters(f), []);

  const allSelected = filteredEmployees.length > 0 && filteredEmployees.every((e) => selected.has(e.id));
  const someSelected = filteredEmployees.some((e) => selected.has(e.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => { const n = new Set(prev); filteredEmployees.forEach((e) => n.delete(e.id)); return n; });
    } else {
      setSelected((prev) => { const n = new Set(prev); filteredEmployees.forEach((e) => n.add(e.id)); return n; });
    }
  };

  const toggle = (id) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSend = async () => {
    const targets = selected.size > 0 ? employees.filter((e) => selected.has(e.id)) : filteredEmployees;
    await sendReminder({
      variables: { type: "download_agent_reminder", options: { employeeEmails: targets.map(getPrimaryEmail).filter(Boolean) } },
    });
  };

  const handleClose = () => {
    setSearch(""); setTableFilters({}); setSelected(new Set()); onClose?.();
  };

  const columns = useMemo(() => [
    {
      label: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={toggleAll}
          size="small"
          sx={{ p: 0, color: theme.customColors.border.main, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: theme.customColors.button.primary } }}
        />
      ),
      field: "checkbox",
      width: "7%",
      align: "center",
    },
    { label: "Employee", field: "employee", width: "47%", align: "left" },
    { label: "Role", field: "role", width: "23%", align: "left", filterConfig: { filterOptions: roleOptions } },
    { label: "Designation", field: "designation", width: "23%", align: "left", filterConfig: { filterOptions: designationOptions } },
  ], [roleOptions, designationOptions, allSelected, someSelected, toggleAll, theme]);

  const renderCell = (row, col) => {
    if (col.field === "checkbox") {
      return (
        <Checkbox
          checked={selected.has(row.id)}
          size="small"
          onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
          sx={{ p: 0, color: theme.customColors.border.main, "&.Mui-checked": { color: theme.customColors.button.primary } }}
        />
      );
    }
    if (col.field === "employee") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Avatar src={row._emp.avatar} sx={{ width: 26, height: 26, fontSize: "10px", flexShrink: 0, bgcolor: theme.customColors.button.primary, color: theme.customColors.button.text }}>
            {getInitials(row._emp.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: theme.customColors.text.strong, fontSize: "0.8rem" }}>
              {row._emp.name}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: theme.customColors.text.secondary, fontSize: "0.72rem" }}>
              {row.email}
            </Typography>
          </Box>
        </Box>
      );
    }
    if (col.field === "role") {
      return row.role
        ? <Typography variant="body2" noWrap sx={{ color: theme.customColors.text.strong, fontSize: "0.8rem" }}>{row.role}</Typography>
        : <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>—</Typography>;
    }
    if (col.field === "designation") {
      return row.designation
        ? <Typography variant="body2" noWrap sx={{ color: theme.customColors.text.strong, fontSize: "0.8rem" }}>{row.designation}</Typography>
        : <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>—</Typography>;
    }
    return null;
  };

  const tableRows = useMemo(() =>
    filteredEmployees.map((emp) => ({
      id: emp.id, _emp: emp,
      email: getPrimaryEmail(emp),
      role: getPrimaryRole(emp),
      designation: getPrimaryJobTitle(emp),
    })),
    [filteredEmployees]
  );

  const sendCount = selected.size > 0 ? selected.size : filteredEmployees.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.05rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          py: 1.75, px: 3,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        Rego WatchTower Agent Installation Reminder
        <IconButton onClick={handleClose} size="small" sx={{ color: theme.customColors.icons.dark }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <>
          {/* Toolbar: search + count + clear */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.customColors.border.main}`, backgroundColor: theme.customColors.background.main, display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#6B7280", fontSize: 17 }} /></InputAdornment>,
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")} edge="end"><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff", borderRadius: "8px", fontSize: "13px",
                    "& fieldset": { borderColor: "#E5E7EB" },
                    "&:hover fieldset": { borderColor: "#9CA3AF" },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6", boxShadow: "0 0 0 3px rgba(59,130,246,0.1)" },
                  },
                }}
              />
              <Box sx={{ px: 2, py: 0.625, borderRadius: "8px", backgroundColor: "#F3F4F6", border: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
                <Typography variant="body2" color="#374151" fontWeight={500} sx={{ fontSize: "0.78rem" }}>
                  {staffLoading ? "…" : `${filteredEmployees.length} members`}
                  {selected.size > 0 && <Box component="span" sx={{ color: "primary.main", ml: 0.5 }}>· {selected.size} selected</Box>}
                </Typography>
              </Box>
              {selected.size > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelected(new Set())}
                  sx={{
                    fontWeight: 500,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "13px",
                    px: 1.5,
                    borderColor: theme.customColors.border.main,
                    color: theme.customColors.text.strong,
                    "&:hover": { borderColor: theme.customColors.border.strong, backgroundColor: theme.customColors.background.hover },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Table */}
            <Box sx={{ maxHeight: 360, overflowY: "auto", overflowX: "hidden" }}>
              <DialogTable
                columns={columns}
                data={tableRows}
                renderCell={renderCell}
                onFilterChange={handleFilterChange}
                filters={tableFilters}
                loading={staffLoading}
                getRowSx={(row) => selected.has(row.id) ? { bgcolor: "#EFF6FF" } : {}}
                getRowProps={(row) => ({ onClick: () => toggle(row.id), sx: { cursor: "pointer" } })}
                emptyMessage="No employees without Rego WatchTower installed."
              />
            </Box>
          </>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, background: theme.customColors.background.cardHeader, borderTop: `1px solid ${theme.customColors.border.main}`, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small"
          sx={{ fontWeight: 500, px: 2.5, borderRadius: 1, borderColor: theme.customColors.border.main, color: theme.customColors.text.strong, "&:hover": { borderColor: theme.customColors.border.strong, backgroundColor: theme.customColors.background.hover } }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} size="small"
          disabled={sending || filteredEmployees.length === 0}
          sx={{ fontWeight: 500, px: 2.5, borderRadius: 1, bgcolor: theme.customColors.button.primary, color: theme.customColors.button.text, "&:hover": { bgcolor: theme.palette._blue?.dark }, "&:disabled": { backgroundColor: theme.customColors.background.light, color: theme.customColors.text.disabled } }}>
          {sending ? <CircularProgress size={14} sx={{ color: theme.customColors.button.text }} /> : `Send Reminder${selected.size > 0 ? ` (${selected.size})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendAgentReminderDialog;
